import React, { useCallback, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    Grid,
} from "@mui/material";
import { useFormik, FormikProvider, Form } from "formik";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb'; // Import GB locale for DD/MM/YYYY format
import debounce from "lodash.debounce";
import { promoCodeInitialValues, ENUMS } from "./Promo.constant";
import { promoCodeValidationSchema } from "./../../util/validationSchemas";
import { useSelectedTeam, useSelectedTeamState } from "../../hooks/useSelectedTeam";
import { fetchWithError } from "../../react-query/fetchWithError";
import { queryKeys } from "../../react-query/queryKeys";
import { useMutate, useGetDetail } from "../../hooks/useFetch.hook";
import { useDispatch } from "react-redux";
import { alertSlice } from "../../store/alert/alert.slice";
import { promoCodesUrl, promoCodeByIdUrl } from "../../react-query/endPoints";
import sanitizeValues from "src/utils/sanitizeValues";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";

const PromoModal = ({ open, onClose, editData = null, viewMode = false }) => {
    const { teamId } = useSelectedTeam();
    const { teamCurrencySymbol } = useSelectedTeamState();
    const dispatch = useDispatch();
    const { mutate, isLoading, isError, error: mutationError } = useMutate("promoCodes");
    const isReadOnly = useAccess(FEATURE.PROMOCODE, "R");

    // Set locale to GB for DD/MM/YYYY format
    dayjs.locale('en-gb');

    // Separate state for API validation error
    const [codeExistsError, setCodeExistsError] = useState("");

    const isEditMode = Boolean(editData) && !viewMode;
    const isViewMode = Boolean(editData) && viewMode;
    const isFormReadOnly = isViewMode || isReadOnly;

    // Store the original usage limit from backend for validation
    const [originalUsageLimit, setOriginalUsageLimit] = useState(0);
    const [hasUsageLimitError, setHasUsageLimitError] = useState(false);
    const [hasValidFromExpired, setHasValidFromExpired] = useState(false);
    const [hasMinTrxValueError, setHasMinTrxValueError] = useState(false);

    // Fetch promo code data when editing or viewing
    const { data: fetchedPromoData, isFetching } = useGetDetail(
        "promoCode",
        { id: editData },
        { enabled: (isEditMode || isViewMode) && Boolean(editData) && open }
    );

    const formik = useFormik({
        initialValues: promoCodeInitialValues(),
        validationSchema: promoCodeValidationSchema(teamCurrencySymbol),
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, setFieldError, setStatus }) => {
            if (isReadOnly) {
                setSubmitting(false);
                return;
            }
            // Check for client-side validation errors before submitting
            const sanitizeData = sanitizeValues(values) 
            if (codeExistsError) {
                setStatus("Please fix the promo code validation error before submitting.");
                setSubmitting(false);
                return;
            }

            if (hasUsageLimitError) {
                setStatus("Please fix the usage limit error before submitting.");
                setSubmitting(false);
                return;
            }

            if (hasMinTrxValueError) {
                setStatus("Please fix the min transaction value error before submitting.");
                setSubmitting(false);
                return;
            }

            // Clear any previous status
            setStatus("");
            
            // Prepare the mutation
            const mutationConfig = isEditMode 
                ? {
                    url: promoCodeByIdUrl(teamId, editData),
                    method: "PUT",
                    body: sanitizeData,
                }
                : {
                    url: promoCodesUrl(teamId),
                    method: "POST",
                    body: (() => {
                        sanitizeData["discount_type"] === "FIXED" && (sanitizeData["max_discount_value"] = sanitizeData?.discount_value);
                        return sanitizeData;
                    })(),
                };

            // Use mutateAsync with proper error handling
            mutate(mutationConfig, {
                onSuccess: (data) => {
                    setSubmitting(false);
                    // Only close modal on success
                    onClose();
                },
                onError: (error) => {
                    setSubmitting(false);
                    console.log("Error detected - keeping modal open:", error);
                    
                    // Extract error message
                    let errorMessage = "Failed to save promo code. Please try again.";
                    if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        errorMessage = error.response.data.error;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    }

                    // Handle field-specific errors
                    if (error?.response?.data?.errors) {
                        const fieldErrors = error.response.data.errors;
                        Object.keys(fieldErrors).forEach(field => {
                            const errorMsg = Array.isArray(fieldErrors[field]) 
                                ? fieldErrors[field][0] 
                                : fieldErrors[field];
                            setFieldError(field, errorMsg);
                        });
                    }
                    
                    // Set form status for error display
                    setStatus(errorMessage);
                }
            });
        },
    });

    // Set form values when fetched data is available
    useEffect(() => {
        if (fetchedPromoData && (isEditMode || isViewMode)) {
            // Store original usage limit for validation
            if (fetchedPromoData.usage_limit) {
                setOriginalUsageLimit(Number(fetchedPromoData.usage_limit));
            }

            // Check if Valid From date has expired
            if (fetchedPromoData.valid_from && dayjs(fetchedPromoData.valid_from).isBefore(dayjs(), 'day')) {
                setHasValidFromExpired(true);
            } else {
                setHasValidFromExpired(false);
            }

            formik.setValues({
                code: fetchedPromoData.code || "",
                name: fetchedPromoData.name || "",
                description: fetchedPromoData.description || "",
                discount_type: fetchedPromoData.discount_type || "PERCENTAGE",
                discount_type_for: fetchedPromoData.discount_type_for || "CHARGING",
                discount_value: fetchedPromoData.discount_value || "",
                max_discount_value: fetchedPromoData.max_discount_value || "",
                min_trx_value: fetchedPromoData.min_trx_value || "",
                valid_from: fetchedPromoData.valid_from || "",
                valid_to: fetchedPromoData.valid_to || "",
                usage_limit: fetchedPromoData.usage_limit || "",
                is_active: Boolean(fetchedPromoData.is_active),
               // usage_frequency: fetchedPromoData.usage_frequency || 1,
            });
        } else if (!editData && open) {
            formik.resetForm();
            setOriginalUsageLimit(0);
            setCodeExistsError("");
            setHasUsageLimitError(false);
            setHasValidFromExpired(false);
            setHasMinTrxValueError(false);
        }
    }, [fetchedPromoData, isEditMode, isViewMode, editData, open]); // Added editData and open to dependencies

    const {
        errors,
        touched,
        values,
        handleSubmit,
        handleChange,
        isSubmitting,
        getFieldProps,
        resetForm,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        setErrors,
        status,
    } = formik;

    // Debounced API call to check promo code
    const checkPromoCode = useCallback(
        debounce(async (code) => {
            if (!code || !teamId || isEditMode) {
                setCodeExistsError(""); // Clear error when no code or in edit mode
                return;
            }
            try {
                const queryKey = queryKeys.checkPromoCode(teamId, code);
                const response = await fetchWithError({ queryKey });
                if (response?.exists) {
                    setCodeExistsError("This code is already in use, try different code");
                } else {
                    setCodeExistsError("");
                }
            } catch (err) {
                console.error("PromoCode check error:", err);
                setCodeExistsError(""); // Clear error on API failure
            }
        }, 500),
        [teamId, isEditMode]
    );

    // Function to validate min transaction value against discount values
    const validateMinTrxValue = useCallback((minTrxValue, discountType, discountValue, maxDiscountValue) => {
        if (!minTrxValue || minTrxValue === 0) {
            setHasMinTrxValueError(false);
            return true;
        }

        const minTrx = Number(minTrxValue);
        const discount = Number(discountValue);
        const maxDiscount = Number(maxDiscountValue);

        if (discountType === 'FIXED' && discount > 0) {
            if (minTrx <= discount) {
                setHasMinTrxValueError(true);
                return false;
            }
        }

        if (discountType === 'PERCENTAGE' && maxDiscount > 0) {
            if (minTrx <= maxDiscount) {
                setHasMinTrxValueError(true);
                return false;
            }
        }

        setHasMinTrxValueError(false);
        return true;
    }, []);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isViewMode ? "View Promo Code" : (isEditMode ? "Edit Promo Code" : "Create New Promo Code")}
            </DialogTitle>
            <FormikProvider value={formik}>
                <Form
                    autoComplete="off"
                    id="company"
                    noValidate
                    onSubmit={handleSubmit}
                >
                    <DialogContent>
                        {status && (
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                    <div style={{ 
                                        padding: '12px', 
                                        backgroundColor: '#ffebee', 
                                        border: '1px solid #f44336', 
                                        borderRadius: '4px',
                                        color: '#c62828',
                                        fontSize: '14px',
                                        marginBottom: '16px'
                                    }}>
                                        {status}
                                    </div>
                                </Grid>
                            </Grid>
                        )}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    name="code"
                                    label="Promo Code"
                                    value={values.code.toUpperCase()}
                                    onChange={(e) => {
                                        const upperValue = e.target.value.toUpperCase();
                                        setFieldValue("code", upperValue);
                                        setCodeExistsError("");
                                        if (!isEditMode) {
                                            checkPromoCode(upperValue);
                                        }
                                    }}
                                    inputProps={{ style: { textTransform: "uppercase" } }}
                                    helperText={
                                        codeExistsError ||
                                        (touched.code && errors.code) ||
                                        ""
                                    }
                                    error={Boolean(codeExistsError || (touched.code && errors.code))}
                                    disabled={isEditMode || isFormReadOnly} // Disable editing promo code in edit/view/read-only mode
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    disabled={isFormReadOnly}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    multiline
                                    rows={3}
                                    value={values.description}
                                    onChange={handleChange}
                                    error={touched.description && Boolean(errors.description)}
                                    helperText={touched.description && errors.description}
                                    disabled={isFormReadOnly}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    select
                                    fullWidth
                                    label="Discount Type"
                                    name="discount_type"
                                    value={values.discount_type}
                                    onChange={handleChange}
                                    disabled={isFormReadOnly || isEditMode}
                                >
                                    {
                                        ENUMS?.discount_type && Object.keys(ENUMS.discount_type).map((key) => (
                                            <MenuItem key={key} value={ENUMS.discount_type[key]}>{ENUMS.discount_type[key]}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    select
                                    fullWidth
                                    name="discount_type_for"
                                    label="Discount Type For"
                                    value={values.discount_type_for}
                                    onChange={handleChange}
                                    disabled={isFormReadOnly || isEditMode}
                                >
                                    {
                                        ENUMS?.discount_type_for && Object.keys(ENUMS.discount_type_for).map((key) => (
                                            <MenuItem key={key} value={ENUMS.discount_type_for[key]}>{ENUMS.discount_type_for[key]}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>

                            {values.discount_type === "PERCENTAGE" ? (
                                <>
                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Discount Value (%)"
                                            name="discount_value"
                                            value={values.discount_value}
                                            onChange={(e) => {
                                                handleChange(e);
                                                // Validate min transaction value when discount value changes
                                                validateMinTrxValue(
                                                    values.min_trx_value,
                                                    values.discount_type,
                                                    e.target.value,
                                                    values.max_discount_value
                                                );
                                            }}
                                            error={touched.discount_value && Boolean(errors.discount_value)}
                                            helperText={touched.discount_value && errors.discount_value}
                                            disabled={isFormReadOnly || isEditMode}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={`Max Discount Value (${teamCurrencySymbol})`}
                                            name="max_discount_value"
                                            value={values.max_discount_value}
                                            onChange={(e) => {
                                                handleChange(e);
                                                // Validate min transaction value when max discount value changes
                                                validateMinTrxValue(
                                                    values.min_trx_value,
                                                    values.discount_type,
                                                    values.discount_value,
                                                    e.target.value
                                                );
                                            }}
                                            error={touched.max_discount_value && Boolean(errors.max_discount_value)}
                                            helperText={touched.max_discount_value && errors.max_discount_value}
                                            disabled={isFormReadOnly || isEditMode}
                                        />
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={`Discount Value (${teamCurrencySymbol})`}
                                        name="discount_value"
                                        value={values.discount_value}
                                        onChange={(e) => {
                                            handleChange(e);
                                            // Validate min transaction value when discount value changes
                                            validateMinTrxValue(
                                                values.min_trx_value,
                                                values.discount_type,
                                                e.target.value,
                                                values.max_discount_value
                                            );
                                        }}
                                        error={touched.discount_value && Boolean(errors.discount_value)}
                                        helperText={touched.discount_value && errors.discount_value}
                                        disabled={isFormReadOnly || isEditMode}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={6}>
                                <TextField
                                    required
                                    fullWidth
                                    type="number"
                                    label="Usage Limit"
                                    name="usage_limit"
                                    value={values.usage_limit}
                                    onChange={(e) => {
                                        const newValue = Number(e.target.value);
                                        // In edit mode, check if new value is below original usage limit
                                        if (isEditMode && originalUsageLimit > 0 && newValue < originalUsageLimit) {
                                            // Show error but still set the value for immediate feedback
                                            setFieldValue("usage_limit", e.target.value);
                                            setFieldError("usage_limit", `Usage limit cannot be less than ${originalUsageLimit}`);
                                            setHasUsageLimitError(true);
                                        } else {
                                            // Clear any previous error and set value
                                            setFieldValue("usage_limit", e.target.value);
                                            setFieldError("usage_limit", "");
                                            setHasUsageLimitError(false);
                                        }
                                    }}
                                    error={touched.usage_limit && Boolean(errors.usage_limit)}
                                    helperText={
                                        (touched.usage_limit && errors.usage_limit) || 
                                        (isEditMode && originalUsageLimit > 0 && hasUsageLimitError ? (
                                            <span style={{ color: '#FF0000' }}>Minimum allowed: {originalUsageLimit}</span>
                                        ) : <span>Minimum allowed: {originalUsageLimit}</span>)
                                    }
                                    disabled={isFormReadOnly}
                                    inputProps={{
                                        min: isEditMode ? originalUsageLimit : 1
                                    }}
                                />
                            </Grid>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <Grid item xs={6}>
                                    <DatePicker
                                        required
                                        label="Valid From"
                                        value={values.valid_from ? dayjs(values.valid_from) : null}
                                        onChange={(newValue) => {
                                            const selectedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                            setFieldValue("valid_from", selectedDate);
                                            setFieldError("valid_from", "");
                                        }}
                                        minDate={dayjs()} // today onwards
                                        disabled={isFormReadOnly || isEditMode}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: !isViewMode && !isEditMode && (touched.valid_from && Boolean(errors.valid_from)),
                                                helperText: !isViewMode && !isEditMode && (touched.valid_from && errors.valid_from) || "Can be today or a future date",
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <DatePicker
                                        required
                                        label="Valid Until"
                                        value={values.valid_to ? dayjs(values.valid_to) : null}
                                        onChange={(newValue) => {
                                            const selectedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                            setFieldValue("valid_to", selectedDate);
                                            
                                            // Check if valid_to is same as or before valid_from
                                            if (values.valid_from && newValue && 
                                                (dayjs(newValue).isSame(dayjs(values.valid_from), 'day') || 
                                                 dayjs(newValue).isBefore(dayjs(values.valid_from), 'day'))) {
                                                
                                                dispatch(alertSlice.actions.setAlert({
                                                    message: "Valid Until must be at least one day after Valid From date.",
                                                    type: "error"
                                                }));
                                                setFieldError("valid_to", "Must be at least one day after Valid From");
                                            } else {
                                                setFieldError("valid_to", "");
                                            }
                                        }}
                                        minDate={values.valid_from 
                                            ? dayjs(values.valid_from).add(1, 'day') // Must be at least one day after valid_from
                                            : dayjs().add(1, 'day') // Or tomorrow if no valid_from set
                                        }
                                        disabled={isFormReadOnly}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: touched.valid_to && Boolean(errors.valid_to),
                                                helperText: (touched.valid_to && errors.valid_to) || 
                                                    (values.valid_from 
                                                        ? "Must be at least one day after Valid From" 
                                                        : "Must be a future date"),
                                            }
                                        }}
                                    />
                                </Grid>
                            </LocalizationProvider>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`Min Transaction Value (${teamCurrencySymbol})`}
                                    name="min_trx_value"
                                    value={values.min_trx_value}
                                    onChange={(e) => {
                                        handleChange(e);
                                        // Validate min transaction value in real-time
                                        validateMinTrxValue(
                                            e.target.value,
                                            values.discount_type,
                                            values.discount_value,
                                            values.max_discount_value
                                        );
                                    }}
                                    error={
                                        hasMinTrxValueError ||
                                        (touched.min_trx_value && Boolean(errors.min_trx_value))
                                    }
                                    helperText={
                                        hasMinTrxValueError ? (
                                            values.discount_type === 'FIXED' 
                                                ? `Min transaction value must be greater than discount value (${teamCurrencySymbol}${values.discount_value})`
                                                : `Min transaction value must be greater than max discount value (${teamCurrencySymbol}${values.max_discount_value})`
                                        ) : (touched.min_trx_value && errors.min_trx_value)
                                    }
                                    disabled={isFormReadOnly || isEditMode}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Usage Frequency"
                                    name="usage_frequency"
                                    value={values.usage_frequency}
                                    onChange={handleChange}
                                    disabled={isViewMode || isEditMode}
                                    helperText={touched.usage_frequency && errors.usage_frequency ? errors.usage_frequency : "Number of times a user can use this promo code on different dates"}
                                    error={touched.usage_frequency && Boolean(errors.usage_frequency)}
                                >
                                </TextField>
                            </Grid> */}
                            <Grid item xs={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    name="is_active"
                                    value={values.is_active}
                                    onChange={handleChange}
                                    disabled={isFormReadOnly || isEditMode}
                                >
                                    <MenuItem value={true}>Active</MenuItem>
                                    <MenuItem value={false}>Inactive</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    {!isReadOnly && (
                        <DialogActions>
                            <Button onClick={onClose} variant="outlined" disabled={isSubmitting || isLoading || isFetching}>
                                {isViewMode ? "Close" : "Cancel"}
                            </Button>
                            {!isViewMode && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting || isLoading || isFetching || hasUsageLimitError || hasMinTrxValueError}
                                >
                                    {isSubmitting || isLoading || isFetching ? "Loading..." : (isEditMode ? "Update Promo Code" : "Create Promo Code")}
                                </Button>
                            )}
                        </DialogActions>
                    )}
                </Form>
            </FormikProvider>
        </Dialog>
    );
};

export default PromoModal;
